import { Badge } from '@/components/shared/Badge';
import { Button } from '@/components/shared/Button';
import { PlayerNameButton } from '@/components/shared/PlayerNameButton';
import { WeaponIcon } from '@/components/shared/WeaponIcon';
import type { DragEvent } from 'react';
import type { EventSlotSummary, Signup, SignupParticipant } from '@/types/domain';

interface SlotCardProps {
  slot: EventSlotSummary;
  currentUserSignup?: Signup;
  partyLabel: string;
  isPartyFull: boolean;
  alternativePartyLabels: string[];
  domId?: string;
  isHighlighted?: boolean;
  canManageRoster?: boolean;
  draggedParticipant?: SignupParticipant | null;
  draggedSourceSlotId?: string;
  isDropTarget?: boolean;
  signupLockedLabel?: string;
  signupLockedHint?: string;
  onJoin: () => void;
  onRemove: () => void;
  onRemoveParticipant?: (participant: SignupParticipant) => void;
  onParticipantDragStart?: (participant: SignupParticipant, sourceSlotId: string) => void;
  onParticipantDragEnd?: () => void;
  onParticipantDrop?: (
    targetSlotId: string,
    payload?: {
      signupId: string;
      sourceSlotId: string;
    },
  ) => void;
  onParticipantDragTargetChange?: (targetSlotId?: string) => void;
}

interface DragPayload {
  signupId: string;
  sourceSlotId: string;
}

const SIGNUP_DRAG_MIME = 'application/x-woof-woof-signup';

function parseDragPayload(event: DragEvent<HTMLElement>): DragPayload | null {
  const raw = event.dataTransfer.getData(SIGNUP_DRAG_MIME) || event.dataTransfer.getData('text/plain');

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as DragPayload;

    if (!parsed.signupId || !parsed.sourceSlotId) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function canAcceptDraggedParticipant(slot: EventSlotSummary, sourceSlotId?: string): boolean {
  if (!sourceSlotId || sourceSlotId === slot.id || !slot.isActive) {
    return false;
  }

  return !slot.isFull || slot.waitlistEnabled;
}

function getJoinLabel(slot: EventSlotSummary, isPartyFull: boolean): string {
  if (!slot.isActive) {
    return 'Slot Inactive';
  }

  if (slot.isFull) {
    if (slot.waitlistEnabled) {
      return 'Join Waitlist';
    }

    return isPartyFull ? 'Party Full' : 'Slot Full';
  }

  return 'Join Slot';
}

function getFullnessHint(slot: EventSlotSummary, partyLabel: string, isPartyFull: boolean, alternativePartyLabels: string[]): string | null {
  if (!slot.isActive) {
    return 'This slot is inactive. Staff can still review the roster here, but members cannot join it.';
  }

  if (!slot.isFull) {
    return null;
  }

  if (slot.waitlistEnabled) {
    return `This slot is full in ${partyLabel}. You can still join the waitlist and be promoted automatically if a spot opens.`;
  }

  if (isPartyFull && alternativePartyLabels.length > 0) {
    return `${partyLabel} is full. Try ${alternativePartyLabels.join(', ')} instead.`;
  }

  if (isPartyFull) {
    return `${partyLabel} is full. Try another party if one opens later.`;
  }

  if (alternativePartyLabels.length > 0) {
    return `This slot is full. Try ${alternativePartyLabels.join(', ')} instead.`;
  }

  return 'This slot is full right now. Try another party or wait for an opening.';
}

function getDropState(
  slot: EventSlotSummary,
  draggedParticipant: SignupParticipant | null | undefined,
  draggedSourceSlotId: string | undefined,
  partyLabel: string,
  isPartyFull: boolean,
): {
  canDrop: boolean;
  hint: string | null;
} {
  if (!draggedParticipant) {
    return {
      canDrop: false,
      hint: null,
    };
  }

  if (!slot.isActive) {
    return {
      canDrop: false,
      hint: 'Inactive slots cannot accept drag-and-drop moves.',
    };
  }

  if (draggedSourceSlotId === slot.id) {
    return {
      canDrop: false,
      hint: `${draggedParticipant.inGameName} is already on this slot.`,
    };
  }

  if (slot.isFull && !slot.waitlistEnabled) {
    return {
      canDrop: false,
      hint: isPartyFull ? `${partyLabel} is full. Drop on another party or a waitlist-enabled slot.` : 'This slot is full and has no waitlist.',
    };
  }

  if (slot.isFull && slot.waitlistEnabled) {
    return {
      canDrop: true,
      hint: `Drop here to move ${draggedParticipant.inGameName} onto this slot waitlist.`,
    };
  }

  return {
    canDrop: true,
    hint: `Drop here to move ${draggedParticipant.inGameName} onto ${slot.weaponName}.`,
  };
}

function renderParticipantRow(
  member: SignupParticipant,
  slotId: string,
  canManageRoster: boolean,
  onRemoveParticipant: SlotCardProps['onRemoveParticipant'],
  onParticipantDragStart: SlotCardProps['onParticipantDragStart'],
  onParticipantDragEnd: SlotCardProps['onParticipantDragEnd'],
  removeLabel: string,
): JSX.Element {
  const canDragParticipant = Boolean(canManageRoster && onParticipantDragStart);
  const startParticipantDrag = (event: DragEvent<HTMLElement>) => {
    if (!canDragParticipant) {
      return;
    }

    event.stopPropagation();

    const payload = JSON.stringify({
      signupId: member.signupId,
      sourceSlotId: slotId,
    } satisfies DragPayload);

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(SIGNUP_DRAG_MIME, payload);
    event.dataTransfer.setData('text/plain', payload);
    const dragPreview = event.currentTarget.closest('.slot-card__roster-item');

    if (dragPreview instanceof HTMLElement) {
      event.dataTransfer.setDragImage(dragPreview, 18, 18);
    }

    onParticipantDragStart?.(member, slotId);
  };

  const endParticipantDrag = () => {
    if (!canDragParticipant) {
      return;
    }

    onParticipantDragEnd?.();
  };

  return (
    <div
      key={member.signupId}
      className={`slot-card__roster-item${canManageRoster ? ' slot-card__roster-item--managed' : ''}`}
    >
      {canManageRoster ? (
        <>
          <div className="slot-card__participant-main">
            <PlayerNameButton
              profileId={member.profileId}
              primaryLabel={member.status === 'waitlist' ? `#${member.queuePosition} ${member.inGameName}` : member.inGameName}
              secondaryLabel=""
              className="slot-card__participant-name"
              compact
              showBadgeCount={false}
            />
          </div>

          <div className="slot-card__participant-tools">
            {canDragParticipant ? (
              <div
                className="slot-card__drag-handle"
                role="button"
                tabIndex={0}
                draggable={canDragParticipant}
                aria-label={`Drag ${member.inGameName} to another slot`}
                title={`Drag ${member.inGameName} to another slot`}
                onDragStart={startParticipantDrag}
                onDragEnd={endParticipantDrag}
              >
                DRAG
              </div>
            ) : null}
          </div>

          {onRemoveParticipant ? (
            <div className="slot-card__roster-actions">
              <Button variant="danger" className="slot-card__roster-button" onClick={() => onRemoveParticipant(member)}>
                {removeLabel}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <PlayerNameButton
          profileId={member.profileId}
          primaryLabel={member.status === 'waitlist' ? `#${member.queuePosition} ${member.inGameName}` : member.inGameName}
          secondaryLabel=""
          className="slot-card__participant-name"
          compact
          showBadgeCount={false}
        />
      )}
    </div>
  );
}

export function SlotCard({
  slot,
  currentUserSignup,
  partyLabel,
  isPartyFull,
  alternativePartyLabels,
  domId,
  isHighlighted = false,
  canManageRoster = false,
  draggedParticipant,
  draggedSourceSlotId,
  isDropTarget = false,
  signupLockedLabel,
  signupLockedHint,
  onJoin,
  onRemove,
  onRemoveParticipant,
  onParticipantDragStart,
  onParticipantDragEnd,
  onParticipantDrop,
  onParticipantDragTargetChange,
}: SlotCardProps): JSX.Element {
  const currentUserSignedHere = currentUserSignup?.eventSlotId === slot.id;
  const currentUserHasOtherSignup = Boolean(currentUserSignup && currentUserSignup.eventSlotId !== slot.id);
  const joinLabel = signupLockedLabel ?? getJoinLabel(slot, isPartyFull);
  const joinDisabled =
    Boolean(signupLockedLabel) ||
    !slot.isActive ||
    currentUserHasOtherSignup ||
    currentUserSignedHere ||
    (slot.isFull && !slot.waitlistEnabled);
  const fullnessHint = getFullnessHint(slot, partyLabel, isPartyFull, alternativePartyLabels);
  const dropState = getDropState(slot, draggedParticipant, draggedSourceSlotId, partyLabel, isPartyFull);
  const showDropHint = canManageRoster && Boolean(draggedParticipant) && Boolean(dropState.hint);
  const updateDropTargetFromEvent = (event: DragEvent<HTMLElement>) => {
    const payload = parseDragPayload(event);
    const canDropFromPayload = canAcceptDraggedParticipant(slot, payload?.sourceSlotId);

    if (!canManageRoster || (!dropState.canDrop && !canDropFromPayload)) {
      return false;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    onParticipantDragTargetChange?.(slot.id);
    return true;
  };

  return (
    <article
      id={domId}
      className={`slot-card${!slot.isActive ? ' slot-card--inactive' : ''}${slot.isFull ? ' slot-card--full' : ''}${
        isDropTarget && dropState.canDrop ? ' slot-card--drop-target' : ''
      }${showDropHint && !dropState.canDrop ? ' slot-card--drop-blocked' : ''}${isHighlighted ? ' slot-card--highlighted' : ''}`}
      onDragEnter={updateDropTargetFromEvent}
      onDragOver={updateDropTargetFromEvent}
      onDrop={(event) => {
        const payload = parseDragPayload(event);
        const canDropFromPayload = canAcceptDraggedParticipant(slot, payload?.sourceSlotId);

        if (!canManageRoster || (!dropState.canDrop && !canDropFromPayload) || !onParticipantDrop) {
          return;
        }

        event.preventDefault();
        onParticipantDragTargetChange?.(undefined);
        onParticipantDrop(slot.id, payload ?? undefined);
      }}
    >
      <div className="slot-card__media">
        <WeaponIcon imagePath={slot.imagePath} label={slot.weaponName} size="lg" />
      </div>

      <div className="slot-card__content">
        <div className="slot-card__header">
          <div>
            <p className="slot-card__role">{slot.roleCategory}</p>
            <h3>{slot.weaponName}</h3>
            {slot.note ? <p className="slot-card__hint">{slot.note}</p> : null}
          </div>
          {currentUserSignedHere ? <Badge tone="success">Your Slot</Badge> : null}
        </div>

        <div className="slot-card__badges">
          <Badge tone={slot.isFull ? 'warning' : 'success'}>
            {slot.joinedCount}/{slot.capacity} joined
          </Badge>
          {slot.isFull && !slot.waitlistEnabled ? <Badge tone="danger">{isPartyFull ? 'Party Full' : 'Slot Full'}</Badge> : null}
          {slot.isFull && slot.waitlistEnabled ? <Badge tone="info">Waitlist Open</Badge> : null}
          {isPartyFull ? <Badge tone="warning">{partyLabel} Full</Badge> : null}
          {!slot.isActive ? <Badge tone="default">Inactive</Badge> : null}
          {slot.waitlistCount > 0 ? <Badge tone="default">{slot.waitlistCount} waitlist</Badge> : null}
        </div>

        <div className="slot-card__actions">
          {currentUserSignedHere ? (
            <Button variant={signupLockedLabel ? 'secondary' : 'danger'} onClick={onRemove} disabled={Boolean(signupLockedLabel)}>
              {signupLockedLabel ?? 'Remove My Signup'}
            </Button>
          ) : (
            <Button variant={slot.isFull ? 'secondary' : 'primary'} onClick={onJoin} disabled={joinDisabled}>
              {joinLabel}
            </Button>
          )}
        </div>

        {currentUserHasOtherSignup ? (
          <p className="slot-card__hint">Remove your current signup before selecting a different role or weapon.</p>
        ) : null}
        {signupLockedHint && !canManageRoster ? <p className="slot-card__hint">{signupLockedHint}</p> : null}
        {fullnessHint ? <p className="slot-card__hint">{fullnessHint}</p> : null}
        {showDropHint ? <p className={`slot-card__drop-hint${dropState.canDrop ? '' : ' slot-card__drop-hint--blocked'}`}>{dropState.hint}</p> : null}

        {slot.joinedMembers.length > 0 ? (
          <div className="slot-card__roster">
            <p className="slot-card__roster-title">Joined</p>
            <div className="slot-card__roster-list">
              {slot.joinedMembers.map((member) =>
                renderParticipantRow(
                  member,
                  slot.id,
                  canManageRoster,
                  onRemoveParticipant,
                  onParticipantDragStart,
                  onParticipantDragEnd,
                  'Kick',
                ),
              )}
            </div>
          </div>
        ) : null}

        {slot.waitlistMembers.length > 0 ? (
          <div className="slot-card__roster">
            <p className="slot-card__roster-title">Waitlist</p>
            <div className="slot-card__roster-list">
              {slot.waitlistMembers.map((member) =>
                renderParticipantRow(
                  member,
                  slot.id,
                  canManageRoster,
                  onRemoveParticipant,
                  onParticipantDragStart,
                  onParticipantDragEnd,
                  'Remove',
                ),
              )}
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
}
